<template>
  <sb-text-input
    ref="searchField"
    v-model:value="searchTerm"
    :placeholder="placeholder || $t('common.search')"
    data-sb-automation="sb-filter-search-next"
    @keyup.enter.stop="onEnterSearch"
    @input="onInputDebounced"
  >
    <template #prepend>
      <i
        aria-hidden="true"
        :class="[$style.filterSearchIcon, 'sb-icon-search']"
        data-sb-automation="filter-selection-search-input-submit-button"
        @click="handleSearch"
      />
    </template>
    <template #append>
      <i
        v-if="searchTerm?.length > 0"
        aria-hidden="true"
        :class="[$style.filterSearchIcon, 'sb-icon-x']"
        data-sb-automation="filter-selection-search-input-clear-button"
        @click="clear"
      />
    </template>
  </sb-text-input>
</template>

<script setup>
import { ref, computed, defineModel, watch, defineProps, defineEmits, nextTick } from 'vue';
import debounce from 'lodash/debounce';
import SbTextInput from '@src/components/Input/SbTextInput.vue';

// Props
const props = defineProps({
  search: String,
  placeholder: {
    type: String,
  },
  minSearchTermLength: {
    type: Number,
    default: 0,
  },
  debounceTime: {
    type: Number,
    default: () => 500,
  },
  isSearchOnEnter: {
    type: Boolean,
  },
});

const emit = defineEmits(['update:search']);
const searchTerm = ref('');
const search = defineModel('search');

watch(search, (newValue) => {
  searchTerm.value = newValue || '';
}, { immediate: true });

const searchField = ref(null);

const minSearchTermLengthMet = computed(() => {
  return searchTerm.value.length >= props.minSearchTermLength;
});

function handleSearch(_, { ignoreSearchLength, ignoreBlur, clearField } = {}) {
  if (searchTerm.value?.length > 0 && !minSearchTermLengthMet.value && !ignoreSearchLength) return;
  emit('update:search', searchTerm.value);
  if (!ignoreBlur) searchField.value?.blur();
  if (clearField) {
    nextTick(() => { search.value = ''; });
  }
}
function onEnterSearch(ev) {
  handleSearch(ev, { ignoreSearchLength: true, ignoreBlur: true, clearField: true });
}

const onInputDebounced = !props.isSearchOnEnter ? debounce(
  (ev) => handleSearch(ev, { ignoreBlur: true }),
  props.debounceTime,
) : undefined;

function clear() {
  searchTerm.value = '';
  handleSearch(null, { ignoreSearchLength: true });
}
</script>

<style module lang="scss">
@use '@src/components/Filter/next/styles/sbSearchBar.module.scss';
</style>
